import {
  Box,
  Button,
  ButtonProps,
  Checkbox,
  Chip,
  ChipProps,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  castArray,
  debounce,
  get,
  isArray,
  isFunction,
  isNull,
  isObject,
  isString,
  isUndefined,
  startCase,
  without,
} from "lodash";
import React, {
  isValidElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Delete,
  Edit,
  MoreVert,
  ExpandMore,
  ExpandLess,
  Search,
} from "@mui/icons-material";
import LoadingOverlay from "../LoadingOverlay";
import { formatDate, formatDateTime } from "../../../utils/labels/formatDateTime";
import EmptyState from "../EmptyState";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DataGridPro,
  DataGridProProps,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_REORDER_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCallbackDetails,
  GridColDef,
  GridColType,
  GridFilterModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowParams,
  GridRowsProp,
  GridSingleSelectColDef,
  gridFilteredDescendantCountLookupSelector,
  gridFilteredSortedRowIdsSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { frFR } from "@mui/x-data-grid/locales";

import {
  CustomField,
  CustomFieldDefinition,
  CustomFieldType,
} from "../../../graphql/generated";
import enumLabel from "../../../utils/labels/enumLabel";
import {
  dateColumnType,
  dateTimeColumnType,
} from "./DataGridDateColumnTypes/DataGridDateColumnTypes";
import DataGridCustomToolbar from "./DataGridCustomToolbar/DataGridCustomToolbar";
import { DataGridCustomRow } from "./DataGridCustomRow/DataGridCustomRow";
import i18next from "i18next";
import { customSelectFilterOperators } from "./customSelectFilterOperators";
import { testIds } from "./LynksTable.testIds";

export type TableFieldType =
  | "string"
  | "select"
  | "number"
  | "date"
  | "datetime"
  | "boolean"
  | "custom";

const tableFieldTypeToDatagridType: Record<TableFieldType, GridColType> = {
  string: "string",
  number: "number",
  date: "date",
  datetime: "dateTime",
  boolean: "boolean",
  custom: "string",
  select: "singleSelect",
};

const TABLE_STATE_PREFIX = "truetms-table-state-v3";

const customFieldDefinitionTypeToDatagridType: Record<
  CustomFieldType,
  GridColType
> = {
  [CustomFieldType.Boolean]: "boolean",
  [CustomFieldType.Date]: "date",
  [CustomFieldType.Datetime]: "dateTime",
  [CustomFieldType.Number]: "number",
  [CustomFieldType.String]: "string",
  [CustomFieldType.Time]: "dateTime",
  [CustomFieldType.Select]: "singleSelect",
  [CustomFieldType.Multiselect]: "singleSelect",
};

/**
 * Calculates an appropriate column width based on the header name length.
 * This ensures columns have adequate space to display their headers without truncation.
 *
 * @param headerName - The column header text
 * @param type - The column type (affects minimum width requirements)
 * @returns A calculated width in pixels
 */
const calculateColumnWidthFromHeader = (
  headerName: string,
  type?: GridColType
): number => {
  // Base calculation: ~8-9px per character + padding
  const baseCharWidth = 8.5;
  const padding = 40; // Left + right padding + icon space
  const calculatedWidth = headerName.length * baseCharWidth + padding;

  // Type-specific minimum widths
  const typeMinWidths: Record<string, number> = {
    dateTime: 180,
    date: 120,
    singleSelect: 150,
    boolean: 100,
    number: 120,
  };

  const minWidth = typeMinWidths[type || ""] || 100;
  const maxWidth = 400; // Prevent extremely wide columns

  return Math.max(minWidth, Math.min(maxWidth, calculatedWidth));
};

export type Renderable = string | number | Date | null | undefined | ReactNode;

export type BasicTableField<T> = {
  value: keyof T | ((item: T) => Renderable);
  rawValue?: (
    item: T
  ) =>
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | Promise<string | number | boolean | Date | null | undefined>;
  // Value used only for CSV export. Use this for expensive/async lookups
  // so the grid (sort/filter/render) never triggers them via rawValue.
  exportValue?: (
    item: T
  ) =>
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | Promise<string | number | boolean | Date | null | undefined>;
  subtitle?: keyof T | ((item: T) => Renderable);
  extra?: keyof T | ((item: T) => Renderable);
  label: string | (() => string);
  type: TableFieldType;
  colors?: never;
  key?: string;
  sortBy?: string | string[];
  hidden?: boolean;
  values?:
    | string[]
    | {
        value: string;
        label: string;
      }[];
  filterable?: boolean;
};

export type EnumTableField<T, E extends string | number | symbol> = {
  value: (item: T) => E | null;
  rawValue?: (
    item: T
  ) =>
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | Promise<string | number | boolean | Date | null | undefined>;
  exportValue?: (
    item: T
  ) =>
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | Promise<string | number | boolean | Date | null | undefined>;
  valueLabel?: (value: E) => string;
  subtitle?: never;
  extra?: never;
  label: string;
  type: "enum";
  colors: {
    [key in E]: ChipProps["color"];
  };
  key?: string;
  sortBy?: string | string[];
  values?: E[];
  filterable?: boolean;
};

export type TableField<T, E extends string | number | symbol = never> =
  | BasicTableField<T>
  | EnumTableField<T, E>;

export type SortField<C extends string> = {
  criteria: C;
  label: string;
};

export type Sort<C> = {
  criteria: C;
  desc: boolean;
};

export type TableAction<T> = {
  icon: ReactNode;
  tooltip: string;
  label?: string;
  isApplicable?: (record: T) => boolean;
  onClick: (record: T) => void;
  secondary?: boolean;
  disabled?: boolean | ((record: T) => boolean);
};

type BasicLynksTableProps<T, C extends string = never> = {
  data: Array<T>;
  loading?: boolean;
  loadingRecordIds?: string[];
  sortFields?: Array<SortField<C>>;
  actions?: Array<TableAction<T>>;
  detailsUrlPrefix?: string | ((record: T) => string);
  compact?: boolean;
  showEmptyStateImage?: boolean;
  stickyHeader?: boolean;
  rowStyle?: (record: T) => SxProps<Theme>;
  /**
   * Additional HTML data attributes applied to each rendered row. This makes
   * row-level integration hooks available without coupling callers to MUI's
   * DataGrid implementation.
   */
  rowDataAttributes?: (
    record: T
  ) => Record<`data-${string}`, string | number | boolean | undefined>;
  disableSearch?: boolean;
  onSortChange?: (sort: Sort<C> | null) => void;
  count?: number | null;
  page?: number;
  perPage?: number;
  selected?: T[];
  onRecordClick?: (record: T) => void;
  onSelect?: (records: T[]) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onRowChange?: (id: string, updatedRecord: T) => void;
  customFieldDefinitions?: Pick<
    CustomFieldDefinition,
    "key" | "type" | "label" | "selectOptions"
  >[];
  customFieldsGetter?: (record: T) => CustomField[];
  id?: string;
  getRecordParentId?: (record: T) => string | null | undefined;
  onFilteredRecordsChange?: (filteredRecords: T[]) => void;
  emptyState?: {
    title?: string;
    message?: string;
    image?: string;
    custom?: JSX.Element;
  };
  onExport?: () => void | Promise<void>;
} & Partial<DataGridProProps>;

type FieldBasedLynksTableProps<
  T,
  E extends string | number | symbol = never,
  C extends string = never
> = BasicLynksTableProps<T, C> & {
  fields: Array<TableField<T, E>>;
  renderRow?: never;
  renderHeader?: never;
  draggable?: boolean;
  onDragStart?: (event: React.DragEvent<HTMLDivElement>, record: T) => void;
  onDragEnd?: (event: React.DragEvent<HTMLDivElement>, record: T) => void;
};

type RenderBasedLynksTableProps<T, C extends string = never> =
  BasicLynksTableProps<T, C> & {
    renderRow: (row: T) => ReactNode;
    renderHeader?: () => ReactNode;
    fields?: never;
    draggable?: never;
    onDragStart?: never;
    onDragEnd?: never;
  };

export type LynksTableProps<
  T,
  E extends string | number | symbol = never,
  C extends string = never
> = FieldBasedLynksTableProps<T, E, C> | RenderBasedLynksTableProps<T, C>;

export default function LynksTable<
  T extends {
    _id: string;
    customFields?: CustomField[] | null;
  },
  E extends string | number | symbol = never,
  C extends string = never
>({
  fields,
  renderRow,
  renderHeader,
  data,
  loading,
  loadingRecordIds,
  sortFields,
  actions,
  detailsUrlPrefix,
  compact,
  showEmptyStateImage,
  stickyHeader,
  disableSearch,
  onSortChange,
  count,
  page,
  perPage,
  selected,
  onRecordClick,
  onSelect,
  onEdit,
  onDelete,
  onRowChange,
  customFieldDefinitions,
  customFieldsGetter,
  id,
  draggable,
  onDragStart,
  onDragEnd,
  rowStyle,
  rowDataAttributes,
  getRecordParentId,
  onFilteredRecordsChange,
  emptyState,
  onSortModelChange,
  onFilterModelChange,
  onPaginationModelChange,
  onExport,
  sx: sxProp,
  ...otherProps
}: LynksTableProps<T, E, C>) {
  const orgId = localStorage.getItem("keycloak::orgId");
  const ORG_TABLE_STATE_PREFIX = useMemo(
    () => `${TABLE_STATE_PREFIX}::${orgId}`,
    [orgId]
  );

  const [sortCriteria] = useState<C | null>(null);
  const [sortDesc] = useState(false);
  const previousCount = useRef(count);

  useEffect(() => {
    if (count !== null) {
      previousCount.current = count;
    }
  }, [count]);

  useEffect(() => {
    if (onSortChange) {
      onSortChange(
        sortCriteria
          ? {
              criteria: sortCriteria,
              desc: sortDesc,
            }
          : null
      );
    }
  }, [sortCriteria, onSortChange, sortDesc]);

  const navigate = useNavigate();

  //  More menu handlers
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMenuRow, setActiveMenuRow] = useState<null | T>(null);
  const isMoreActionsMenuOpened = Boolean(menuAnchorEl);

  const handleActionMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, row: T) => {
      setMenuAnchorEl(event.currentTarget);
      setActiveMenuRow(row);
    },
    []
  );

  const handleActionMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setActiveMenuRow(null);
  }, []);

  const renderValue = useCallback(
    (row: T, field: TableField<T, E>) => {
      const cellValue =
        field.value instanceof Function ? field.value(row) : row[field.value];
      if (isValidElement(cellValue)) {
        return cellValue;
      }
      if (field.type === "boolean") {
        return (
          <Checkbox
            color="primary"
            disabled={field.value instanceof Function || !onRowChange}
            checked={!!cellValue}
            onChange={(event, checked) => {
              if (field.value instanceof Function) {
                // We can't guess the key, this is a custom function field value
                return;
              }
              onRowChange?.(row._id, {
                ...row,
                [field.value]: checked,
              });
            }}
          inputProps={{ "data-testid": testIds.lynksTableBooleanField } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        );
      }
      if (cellValue == null) {
        return null;
      }
      if (field.type === "string") {
        return String(cellValue);
      }
      if (field.type === "datetime") {
        return formatDateTime(String(cellValue));
      }
      if (field.type === "date") {
        return formatDate(String(cellValue), null, false);
      }
      if (field.type === "enum") {
        const enumValue = field.value(row);
        if (enumValue === null) {
          return null;
        }
        return (
          <Chip
            label={
              field.valueLabel
                ? field.valueLabel(enumValue)
                : startCase(String(cellValue))
            }
            color={field.colors[enumValue]}
            component="span"
          />
        );
      }
      if (field.type === "select") {
        return isArray(cellValue) ? cellValue.join(", ") : String(cellValue);
      }
      return cellValue as ReactNode;
    },
    [onRowChange]
  );

  const renderSubtitle = useCallback((row: T, field: TableField<T, E>) => {
    if (!field.subtitle) {
      return null;
    }
    const subtitle =
      field.subtitle instanceof Function
        ? field.subtitle(row)
        : row[field.subtitle];
    if (subtitle === null) {
      return null;
    }
    return isValidElement(subtitle) ? subtitle : String(subtitle);
  }, []);

  const renderExtra = useCallback((row: T, field: TableField<T, E>) => {
    if (!field.extra) {
      return null;
    }
    const extra =
      field.extra instanceof Function ? field.extra(row) : row[field.extra];
    if (extra === null) {
      return null;
    }
    return isValidElement(extra) ? extra : String(extra);
  }, []);

  const renderSelectionCheckbox = (row: T) => {
    return onSelect ? (
      <Checkbox
        color="primary"
        checked={selected?.includes(row) || false}
        inputProps={{ "data-testid": `${testIds.lynksTableSelectRow}-${row._id}` } as React.InputHTMLAttributes<HTMLInputElement>}
        onClick={(event) => event.stopPropagation()}
        onChange={(event, checked) => {
          event.stopPropagation();
          if (checked) {
            onSelect((selected || []).concat(row));
          } else {
            onSelect(without(selected, row));
          }
        }}
      />
    ) : null;
  };

  const renderField = useCallback(
    (row: T, field: TableField<T, E>): ReactNode => {
      const value = renderValue(row, field);
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
          draggable={draggable}
          // onDragStart={(e) => {
          //   e.dataTransfer.setData("text/plain", JSON.stringify(row));
          //   onDragStart?.(e, row);
          // }}
          // onDragEnd={(e) => {
          //   onDragEnd?.(e, row);
          // }}
        >
          <Box>
            {field.type === "custom" || isValidElement(value) ? (
              renderValue(row, field)
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: field.subtitle ? "bold" : "normal",
                }}
              >
                {renderValue(row, field)}
              </Typography>
            )}
            <Typography variant="subtitle2">
              {renderSubtitle(row, field)}
            </Typography>
          </Box>
          {field.extra ? (
            <Box sx={{ pl: 1 }}>{renderExtra(row, field)}</Box>
          ) : null}
        </Box>
      );
    },
    [draggable, renderExtra, renderSubtitle, renderValue]
  );

  const hasActions = useMemo(
    () => !!(onEdit || onDelete || actions?.length),
    [onEdit, onDelete, actions]
  );

  const renderEmptyState = () => {
    return !loading && !data.length ? (
      emptyState && emptyState.custom ? (
        emptyState.custom
      ) : (
        <EmptyState
          title={
            emptyState
              ? emptyState.title
              : sortCriteria
              ? t("list.noResultFound", "No result found")
              : t("list.nothingHereYet", "Nothing here yet")
          }
          message={
            emptyState
              ? emptyState.message
              : sortCriteria
              ? t(
                  "list.emptyStateDescription",
                  "Try adjusting your search or filter to find what you are looking for."
                )
              : ""
          }
          showImage={showEmptyStateImage}
          image={emptyState?.image}
        />
      )
    ) : null;
  };

  const renderRowLoadingOverlay = (row: T) => {
    return <LoadingOverlay loading={!!loadingRecordIds?.includes(row._id)} />;
  };

  const initialState: GridInitialState | undefined = useMemo(
    () => {
      const saved = localStorage.getItem(`${ORG_TABLE_STATE_PREFIX}::${id}`)
        ? JSON.parse(
            localStorage.getItem(`${ORG_TABLE_STATE_PREFIX}::${id}`) || "{}"
          )
        : undefined;

      // Build default visibility from fields with hidden: true,
      // but only for columns not already saved by the user
      const savedVisibility = saved?.columns?.columnVisibilityModel || {};
      const defaultHiddenFields = (fields || []).reduce<Record<string, boolean>>(
        (acc, field) => {
          if (!("hidden" in field) || !field.hidden) return acc;
          const fieldName = field.key
            ? field.key
            : field.value instanceof Function
            ? field.sortBy
              ? (Array.isArray(field.sortBy) ? field.sortBy : [field.sortBy]).join("-")
              : field.label instanceof Function
              ? field.label()
              : field.label
            : String(field.value);
          if (!(fieldName in savedVisibility)) {
            acc[fieldName] = false;
          }
          return acc;
        },
        {}
      );

      if (!saved && Object.keys(defaultHiddenFields).length === 0) {
        return undefined;
      }

      return {
        ...saved,
        columns: {
          ...saved?.columns,
          columnVisibilityModel: {
            ...defaultHiddenFields,
            ...savedVisibility,
          },
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, ORG_TABLE_STATE_PREFIX]
  );
  const rows: GridRowsProp<T> = data;
  const apiRef = useGridApiRef();

  const customFieldsColumns: GridColDef<T>[] = useMemo(
    () =>
      (customFieldDefinitions || []).map((customFieldDefinition) => {
        const columnType =
          customFieldDefinitionTypeToDatagridType[customFieldDefinition.type];
        const gridCol: GridColDef<T> = {
          field: `customFields.${customFieldDefinition.key}`,
          cellClassName: `customFields.${customFieldDefinition.key}`,
          type: columnType,
          sortable: true,
          headerName: customFieldDefinition.label,
          valueGetter(value, row) {
            const computedValue = (
              customFieldsGetter?.(row) || row.customFields
            )?.find(
              (customField) => customField.key === customFieldDefinition.key
            )?.value;
            if (
              customFieldDefinition.type === CustomFieldType.Date ||
              customFieldDefinition.type === CustomFieldType.Datetime
            ) {
              return computedValue ? new Date(computedValue) : null;
            }
            return computedValue;
          },
          valueFormatter: (value) => {
            if (value === null || value === undefined) {
              return "";
            }
            if (customFieldDefinition.type === CustomFieldType.Select) {
              return enumLabel(value);
            }
            if (customFieldDefinition.type === CustomFieldType.Multiselect) {
              return castArray(value).map(enumLabel).join(", ");
            }
            return value;
          },
          ...(customFieldDefinition.type === CustomFieldType.Date
            ? dateColumnType
            : customFieldDefinition.type === CustomFieldType.Datetime
            ? dateTimeColumnType
            : {}),
        };

        // Apply stored width if available, otherwise calculate based on header name
        const storedColumnConfig = (initialState?.columns as any)?.lookup?.[
          gridCol.field
        ];
        if (storedColumnConfig?.width) {
          gridCol.width = storedColumnConfig.width;
          gridCol.flex = undefined;
        } else {
          // Calculate width based on header name length
          const calculatedWidth = calculateColumnWidthFromHeader(
            gridCol.headerName || "",
            gridCol.type
          );
          gridCol.minWidth = calculatedWidth;
          gridCol.flex = 1;
        }

        // Set valueOptions for singleSelect columns (Select and Multiselect types)
        if (
          customFieldDefinition.type === CustomFieldType.Select ||
          customFieldDefinition.type === CustomFieldType.Multiselect
        ) {
          (gridCol as GridSingleSelectColDef).valueOptions =
            customFieldDefinition.selectOptions?.map((option) => option.key);
        }

        return gridCol;
      }),
    [customFieldDefinitions, customFieldsGetter, initialState]
  );

  const parseBooleanValue = (
    value:
      | string
      | number
      | boolean
      | symbol
      | React.ReactElement<any, string | React.JSXElementConstructor<any>>
      | React.ReactFragment
      | Date
      | null
      | undefined
  ): boolean | null => {
    switch (true) {
      case isNull(value) || isUndefined(value):
        return null;
      case typeof value === "string":
        return value === "true";
      case typeof value === "boolean":
        return value;
      default:
        return !!value;
    }
  };

  const columns: GridColDef<T>[] = useMemo(
    () =>
      (
        (fields || []).map((field, index) => {
          const fieldName = field.key
            ? field.key
            : field.value instanceof Function
            ? field.sortBy
              ? castArray(field.sortBy).join("-")
              : field.label instanceof Function
              ? field.label()
              : field.label
            : String(field.value);

          const gridCol: GridColDef<T> = {
            field: fieldName,
            cellClassName: castArray(field.sortBy).join("-"),
            type:
              field.type === "enum" || field.type === "select"
                ? "singleSelect"
                : tableFieldTypeToDatagridType[field.type],
            headerName:
              field.label instanceof Function ? field.label() : field.label,
            valueGetter(value, row) {
              if (field.rawValue) {
                return field.rawValue(row);
              }
              const computedValue = field.sortBy
                ? castArray(field.sortBy)
                    .map((sortByItem) => get(row, sortByItem))
                    .join(" ")
                : field.value instanceof Function
                ? field.value(row)
                : value;
              const subtitle = field.subtitle
                ? field.subtitle instanceof Function
                  ? field.subtitle(row)
                  : row[field.subtitle]
                : null;
              if (field.type === "datetime" || field.type === "date") {
                return computedValue &&
                  (typeof computedValue === "string" ||
                    typeof computedValue === "number")
                  ? new Date(computedValue)
                  : null;
              }
              if (field.type === "boolean") {
                return parseBooleanValue(computedValue);
              }
              if (field.type === "select") {
                return computedValue;
              }
              if (computedValue === null) {
                return null;
              }
              return `${String(computedValue)} ${subtitle || ""}`.trim();
            },
            ...(field.value instanceof Function
              ? {
                  renderCell: (params) => {
                    return params.row._id ? renderField(params.row, field) : "";
                  },
                }
              : {}),
            filterable: !!(field.filterable === false
              ? field.filterable
              : field.sortBy ||
                isString(field.value) ||
                field.type !== "string"),
            sortable: !!(
              field.sortBy ||
              isString(field.value) ||
              field.type !== "string"
            ),
            ...(field.type === "datetime"
              ? dateTimeColumnType
              : field.type === "date"
              ? dateColumnType
              : {}),
          };

          // Apply stored width if available, otherwise calculate based on header name
          const storedColumnConfig = (initialState?.columns as any)?.lookup?.[
            gridCol.field
          ];
          if (storedColumnConfig?.width) {
            gridCol.width = storedColumnConfig.width;
            gridCol.flex = undefined;
          } else {
            // Calculate width based on header name length
            const calculatedWidth = calculateColumnWidthFromHeader(
              gridCol.headerName || "",
              gridCol.type
            );
            gridCol.minWidth = calculatedWidth;
            gridCol.flex = 1;
          }
          if (field.type === "select" || field.type === "enum") {
            gridCol.filterOperators = customSelectFilterOperators;
            (gridCol as GridSingleSelectColDef).valueOptions = (
              field.values || []
            ).map((v) =>
              field.type === "enum"
                ? {
                    value: v,
                    label: enumLabel(String(v)),
                  }
                : isObject(v)
                ? v
                : String(v)
            );
          }

          return gridCol;
        }) as GridColDef<T>[]
      ).concat(customFieldsColumns),
    [customFieldsColumns, fields, initialState, renderField]
  );

  const groupingColDef: DataGridProProps["groupingColDef"] = useMemo(() => {
    // Read fresh state for grouping column width persistence
    const freshState =
      id && localStorage.getItem(`${ORG_TABLE_STATE_PREFIX}::${id}`)
        ? JSON.parse(
            localStorage.getItem(`${ORG_TABLE_STATE_PREFIX}::${id}`) || "{}"
          )
        : null;

    const storedColumnConfig = (freshState?.columns as any)?.lookup?.[
      GRID_TREE_DATA_GROUPING_FIELD
    ];

    return {
      headerName: "Expand",
      renderCell: (params: GridRenderCellParams) => (
        <CustomGridTreeDataGroupingCell {...params} />
      ),
      width: storedColumnConfig?.width || 100,
      flex: storedColumnConfig?.width ? undefined : 0,
    };
  }, [id, ORG_TABLE_STATE_PREFIX]);

  const theme = useTheme();
  const displayInList = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation("common");

  const getRowId = useCallback((row: T) => row._id, []);

  const debouncedSave = useMemo(
    () =>
      debounce((state: any) => {
        if (id) {
          localStorage.setItem(
            `${ORG_TABLE_STATE_PREFIX}::${id}`,
            JSON.stringify(state)
          );
        }
      }, 300),
    [ORG_TABLE_STATE_PREFIX, id]
  );

  const handleRowClick = useCallback(
    (row: T) => {
      // Flush any pending saves to ensure column widths are saved before dialog opens
      debouncedSave.flush();

      if (detailsUrlPrefix) {
        if (isFunction(detailsUrlPrefix)) {
          navigate(detailsUrlPrefix(row));
        } else {
          navigate(`${detailsUrlPrefix}/${row._id}`);
        }
      } else if (onRecordClick) {
        onRecordClick(row);
      }
    },
    [onRecordClick, navigate, detailsUrlPrefix, debouncedSave]
  );

  const onRowClick = useCallback(
    (params: GridRowParams<T>) => {
      onSelect?.([params.row as T]);
      handleRowClick(params.row as T);
    },
    [handleRowClick, onSelect]
  );

  const onStateChange = useCallback(
    (state: any) => {
      debouncedSave(state);
    },
    [debouncedSave]
  );

  const onFilterModelChangeInternal = useCallback(
    (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
      if (onFilteredRecordsChange) {
        // Because of a bug in DataGridPro, we need to delay the call to let the grid apply the filter first
        setTimeout(() => {
          if (!apiRef.current) {
            return;
          }
          const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef);
          const filteredRecords = data.filter((row) =>
            visibleRowIds.includes(row._id)
          );
          onFilteredRecordsChange(filteredRecords);
        }, 0);
      }
      onFilterModelChange?.(filterModel, details);
    },
    [apiRef, data, onFilterModelChange, onFilteredRecordsChange]
  );

  // Trigger onFilteredRecordsChange when data changes (not just when filter changes)
  useEffect(() => {
    if (onFilteredRecordsChange && apiRef.current) {
      // Delay to ensure the grid has processed the data update
      setTimeout(() => {
        if (!apiRef.current) {
          return;
        }
        const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef);
        const filteredRecords = data.filter((row) =>
          visibleRowIds.includes(row._id)
        );
        onFilteredRecordsChange(filteredRecords);
      }, 0);
    }
  }, [data, apiRef, onFilteredRecordsChange]);

  useEffect(() => {
    if (initialState) {
      return;
    }
    setTimeout(() => {
      const columns = apiRef.current?.getAllColumns?.() || [];
      const columnsComputedWidths = columns.map(
        (column) => column.computedWidth
      );
      apiRef.current?.updateColumns?.(
        columns.map((col) => ({
          ...col,
          flex: undefined,
        }))
      );
      columns.forEach((column, index) => {
        apiRef.current?.setColumnWidth?.(
          column.field,
          columnsComputedWidths[index]
        );
      });
    });
  }, [apiRef, initialState]);

  const initialSortingFilteringApplied = useRef(false);

  useEffect(() => {
    if (initialSortingFilteringApplied.current) {
      return;
    }
    if (!initialState) {
      return;
    }
    if (
      initialState.pagination?.paginationModel?.page !== undefined &&
      initialState.pagination?.paginationModel?.pageSize !== undefined
    ) {
      onPaginationModelChange?.(
        {
          page: initialState.pagination.paginationModel.page,
          pageSize: initialState.pagination.paginationModel.pageSize,
        },
        {
          api: apiRef.current,
        }
      );
    }

    if (initialState.sorting?.sortModel) {
      const sortModel = initialState.sorting.sortModel;
      onSortModelChange?.(sortModel, {
        api: apiRef.current,
      });
    }

    if (initialState.filter?.filterModel) {
      const filterModel = initialState.filter.filterModel;
      onFilterModelChange?.(filterModel, {
        api: apiRef.current,
      });
    }
    initialSortingFilteringApplied.current = true;
  }, [
    apiRef,
    initialState,
    onPaginationModelChange,
    onSortModelChange,
    onFilterModelChange,
  ]);

  const initialSelectionApplied = useRef(false);

  useEffect(() => {
    if (initialSelectionApplied.current) {
      return;
    }
    if (!apiRef.current.selectRows) {
      return;
    }
    apiRef.current.selectRows((selected || []).map((row) => row._id));
    initialSelectionApplied.current = true;
  }, [selected, apiRef]);

  // Clear selection when data changes
  // TODO: to be troubleshooted, table get refresh and remove selections
  // useEffect(() => {
  //   apiRef.current?.setRowSelectionModel?.([]);
  // }, [data, apiRef]);

  const renderActions = useCallback(
    (row: T) => {
      const rowIndex = data.indexOf(row);
      return (
        <ClickAwayListener onClickAway={handleActionMenuClose}>
          <Box
            sx={{
              display: "flex",
              flexDirection: displayInList ? "column" : "row",
              alignItems: "center",
            }}
          >
            {onEdit && (
              <Tooltip
                title={t("list.edit", "Edit")}
              >
                <IconButton data-testid={testIds.lynksTableEdit}
                  id={id ? `${id}-row-${rowIndex}-action-edit` : undefined}
                  onClick={(e) => {
                    onEdit(row._id);
                    e.stopPropagation();
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {actions
              ? actions
                  .filter((action) =>
                    action.isApplicable ? action.isApplicable(row) : true
                  )
                  .filter((action) => !action.secondary)
                  .map((action, index) => {
                    const isDisabled =
                      typeof action.disabled === "function"
                        ? action.disabled(row)
                        : action.disabled || false;

                    return (
                      <Tooltip
                        title={action.tooltip}
                        key={(action.label || action.tooltip) + index}
                      >
                        {action.label ? (
                          <span>
                            <Button data-testid={`${testIds.lynksTableLabeledRowAction}-${action.label}`}
                              variant="outlined"
                              onClick={(e) => {
                                if (!isDisabled) {
                                  action.onClick(row);
                                }
                                e.stopPropagation();
                              }}
                              startIcon={action.icon}
                              sx={{
                                ml: 3,
                                opacity: isDisabled ? 0.4 : 1,
                                cursor: isDisabled ? "not-allowed" : "pointer",
                              }}
                              size="small"
                              disabled={isDisabled}
                              id={
                                id
                                  ? `${id}-row-${rowIndex}-action-${action.label}`
                                  : undefined
                              }
                            >
                              {action.label}
                            </Button>
                          </span>
                        ) : (
                          <span>
                            <IconButton data-testid={`${testIds.lynksTableIconRowAction}-${action.tooltip}`}
                              id={
                                id
                                  ? `${id}-row-${rowIndex}-action-${action.tooltip}`
                                  : undefined
                              }
                              onClick={(e) => {
                                if (!isDisabled) {
                                  action.onClick(row);
                                }
                                e.stopPropagation();
                              }}
                              disabled={isDisabled}
                              sx={{
                                opacity: isDisabled ? 0.4 : 1,
                                cursor: isDisabled ? "not-allowed" : "pointer",
                              }}
                            >
                              {action.icon}
                            </IconButton>
                          </span>
                        )}
                      </Tooltip>
                    );
                  })
              : null}
            {onDelete && (
              <Tooltip
                title={t("delete", "Delete")}
              >
                <IconButton data-testid={testIds.lynksTableDelete}
                  id={id ? `${id}-row-${rowIndex}-action-delete` : undefined}
                  onClick={(e) => {
                    onDelete(row._id);
                    e.stopPropagation();
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
            {actions &&
            actions
              .filter((action) =>
                action.isApplicable ? action.isApplicable(row) : true
              )
              .some((action) => !!action.secondary) ? (
              <Tooltip
                title={t("list.moreActions", "More actions")}
                key={"more"}
              >
                <div>
                  <IconButton data-testid={testIds.lynksTableMoreActions}
                    aria-controls={
                      isMoreActionsMenuOpened ? "basic-menu" : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={isMoreActionsMenuOpened ? "true" : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleActionMenuClick(event, row);
                    }}
                    id={id ? `${id}-row-${rowIndex}-action-more` : undefined}
                  >
                    <MoreVert />
                  </IconButton>
                </div>
              </Tooltip>
            ) : null}
          </Box>
        </ClickAwayListener>
      );
    },
    [
      data,
      handleActionMenuClose,
      displayInList,
      onEdit,
      t,
      isMoreActionsMenuOpened,
      id,
      actions,
      onDelete,
      handleActionMenuClick,
    ]
  );

  const columnsWithActions = useMemo(() => {
    // Read FRESH state from localStorage instead of using cached initialState
    const freshState =
      id && localStorage.getItem(`${ORG_TABLE_STATE_PREFIX}::${id}`)
        ? JSON.parse(
            localStorage.getItem(`${ORG_TABLE_STATE_PREFIX}::${id}`) || "{}"
          )
        : null;

    // Define actionsColumn INSIDE the useMemo to avoid dependency issues
    const storedActionsConfig = (freshState?.columns as any)?.lookup?.actions;
    const actionsColumn: GridColDef<T> = {
      field: "actions",
      headerName: "Actions",
      renderCell: (params) => {
        const row = params.row as T;
        return row._id ? renderActions(row) : "";
      },
      filterable: false,
      ...(storedActionsConfig?.width
        ? {
            width: storedActionsConfig.width,
            flex: undefined,
          }
        : {
            minWidth: calculateColumnWidthFromHeader("Actions"),
            flex: 1,
          }),
    };

    const baseColumns = [
      GRID_REORDER_COL_DEF,
      GRID_CHECKBOX_SELECTION_COL_DEF,
      ...columns,
      ...(hasActions ? [actionsColumn] : []),
    ];

    const mappedColumns = baseColumns.map((column) => {
      const storedColumnConfig = (freshState?.columns as any)?.lookup?.[
        column.field
      ];

      // If we have stored width, use it and remove flex
      if (storedColumnConfig?.width) {
        return {
          ...column,
          width: storedColumnConfig.width,
          flex: undefined, // Remove flex when using fixed width
        };
      }

      // Otherwise use flex for responsive behavior, preserving minWidth if set
      return {
        ...column,
        flex: column.flex ?? 1,
        // Preserve minWidth from column definition (calculated from header name)
        minWidth: column.minWidth,
      };
    });

    // Restore saved column order from localStorage.
    // The reorder and checkbox system columns are always pinned to the first
    // two positions so they are never displaced by a stale saved column order.
    const PINNED_SYSTEM_FIELDS = [
      GRID_REORDER_COL_DEF.field,
      GRID_CHECKBOX_SELECTION_COL_DEF.field,
    ];
    const orderedFields = (freshState?.columns as any)?.orderedFields;
    if (orderedFields && Array.isArray(orderedFields)) {
      const fieldOrderMap = new Map<string, number>(
        orderedFields.map((field: string, index: number) => [field, index])
      );
      // Separate pinned system columns from the rest so they always stay first.
      const pinnedCols = PINNED_SYSTEM_FIELDS.map((field) =>
        mappedColumns.find((c) => c.field === field)
      ).filter(Boolean) as typeof mappedColumns;
      const restCols = mappedColumns.filter(
        (c) => !PINNED_SYSTEM_FIELDS.includes(c.field)
      );
      restCols.sort((a, b) => {
        const orderA = fieldOrderMap.get(a.field) ?? Number.MAX_SAFE_INTEGER;
        const orderB = fieldOrderMap.get(b.field) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      mappedColumns.splice(0, mappedColumns.length, ...pinnedCols, ...restCols);
    }

    // Check if all data columns have fixed widths - if so, force at least one to flex
    // to prevent blank space when saved widths don't fill container
    const dataColumns = mappedColumns.slice(2);
    const allHaveFixedWidths = dataColumns.every(
      (col) => typeof col.width === "number" && col.flex === undefined
    );

    if (allHaveFixedWidths && dataColumns.length > 0) {
      // Force last data column to flex to fill remaining space
      const lastDataColumnIndex = hasActions
        ? mappedColumns.length - 2 // Before actions column
        : mappedColumns.length - 1;

      if (lastDataColumnIndex >= 2) {
        const lastColumn = mappedColumns[lastDataColumnIndex];
        mappedColumns[lastDataColumnIndex] = {
          ...lastColumn,
          flex: 1,
          width: undefined,
          minWidth: lastColumn.width || 150,
        };
      }
    }

    return mappedColumns;
  }, [columns, hasActions, renderActions, id, ORG_TABLE_STATE_PREFIX]);

  return (
    <Grid
      id={id}
      sx={{
        height: "100%",
      }}
    >
      {displayInList && fields ? (
        <Grid sx={{ position: "relative" }}>
          <LoadingOverlay loading={loading || false} />
          <List aria-label="shipments list">
            <List>
              {data.map((row, i) => (
                <Box>
                  <ListItemButton
                    onClick={() => handleRowClick(row)}
                    key={i}
                    selected={selected?.includes(row) || false}
                    {...rowDataAttributes?.(row)}
                  >
                    {renderSelectionCheckbox(row)}
                    {renderRowLoadingOverlay(row)}
                    <List
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                      }}
                    >
                      {fields.map((field, index) => (
                        <ListItem key={String(field.value || index)}>
                          {renderField(row, field)}
                        </ListItem>
                      ))}
                    </List>
                    <List>{renderActions(row)}</List>
                  </ListItemButton>
                  <Divider />
                </Box>
              ))}
            </List>
          </List>
          {renderEmptyState()}
        </Grid>
      ) : fields ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <DataGridPro
            localeText={
              i18next.language === "fr"
                ? {
                    ...frFR.components.MuiDataGrid.defaultProps.localeText,
                    columnsManagementReset: t("list.resetColumns", "Reset"),
                  }
                : undefined
            }
            rows={rows}
            columns={columnsWithActions}
            getRowId={getRowId}
            apiRef={apiRef}
            checkboxSelection={!!onSelect}
            isRowSelectable={(row: GridRowParams<T>) => !!row.row._id}
            // autoHeight
            onRowSelectionModelChange={(selected) => {
              const selectedIDs = new Set(selected);
              const selectedRows = rows.filter((row) =>
                selectedIDs.has(row._id)
              );
              onSelect?.(selectedRows);
            }}
            onRowClick={onRowClick}
            sx={[
              (theme) => ({
                mt: 1,
                ".MuiDataGrid-columnHeader": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "& path": {
                    fill: theme.palette.primary.contrastText,
                  },
                },
                ".MuiDataGrid-row": {},
                ".MuiDataGrid-filler": {
                  flex: "none !important",
                  width: "0 !important",
                },
                ".MuiDataGrid-cell": {
                  display: "flex",
                },
                minHeight: 200,
              }),
              ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
            ]}
            pagination
            // disableVirtualization
            initialState={id ? initialState : undefined}
            onStateChange={onStateChange}
            loading={loading}
            slots={{
              toolbar: DataGridCustomToolbar,
              row: DataGridCustomRow,
              noRowsOverlay: renderEmptyState,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: !disableSearch,
                quickFilterProps: {
                  quickFilterParser: (searchInput) => [searchInput],
                },
                onExport,
              },
              // @ts-expect-error
              row: {
                rowDataAttributes,
                onDragStart: (e: React.DragEvent<HTMLDivElement>, row: T) => {
                  e.dataTransfer.setData("text/plain", JSON.stringify(row));
                  onDragStart?.(e, row);
                },
                onDragEnd: (e: React.DragEvent<HTMLDivElement>, row: T) => {
                  onDragEnd?.(e, row);
                },
              },
            }}
            treeData={!!getRecordParentId}
            getTreeDataPath={(row) => {
              // Build the full hierarchical path by traversing up the parent chain
              const path: string[] = [];
              let currentRow = row;
              const visited = new Set<string>();
              
              // Traverse up the parent chain to build the path
              while (currentRow) {
                // Prevent infinite loops
                if (visited.has(currentRow._id)) {
                  console.error('Circular reference detected in tree data path for row:', currentRow._id);
                  break;
                }
                visited.add(currentRow._id);
                
                const parentId = getRecordParentId?.(currentRow);
                if (parentId) {
                  // Find the parent row in the data
                  const parentRow = rows.find((r) => r._id === parentId);
                  if (parentRow) {
                    path.unshift(parentId);
                    currentRow = parentRow;
                  } else {
                    // Parent not found in data, stop traversing
                    break;
                  }
                } else {
                  // No parent, we've reached the root
                  break;
                }
              }
              
              // Add the current row's ID at the end
              path.push(row._id);
              
              return path;
            }}
            groupingColDef={groupingColDef}
            onPaginationModelChange={onPaginationModelChange}
            onSortModelChange={onSortModelChange}
            onFilterModelChange={onFilterModelChangeInternal}
            {...otherProps}
          />
        </Box>
      ) : renderRow ? (
        <Stack spacing={2}>
          <TextField
            label={t("search", "Search")}
            InputProps={{
              startAdornment: <Search />,
            }}
            onChange={(event) => {
              onFilterModelChange?.(
                {
                  items: [],
                  quickFilterValues: [event.target.value],
                },
                {
                  api: apiRef.current,
                }
              );
            }}
            sx={{
              alignSelf: "flex-start",
            }}
          inputProps={{ "data-testid": testIds.lynksTableSearch } as React.InputHTMLAttributes<HTMLInputElement>}
          />
          {renderHeader?.()}
          {data.map((record) => renderRow(record))}
          <Pagination
            sx={{
              alignSelf: "flex-end",
              mb: 10,
            }}
            count={Math.ceil((count || 0) / (perPage || 1))}
            page={page || 1}
            onChange={(event, page) => {
              onPaginationModelChange?.(
                {
                  page: page - 1,
                  pageSize: perPage || 0,
                },
                {
                  api: apiRef.current,
                }
              );
            }}
          />
        </Stack>
      ) : null}

      <Menu
        id="basic-menu"
        anchorEl={menuAnchorEl}
        open={isMoreActionsMenuOpened}
        onClose={handleActionMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {activeMenuRow && actions
          ? actions
              .filter((action) =>
                action.isApplicable ? action.isApplicable(activeMenuRow) : true
              )
              .filter((action) => !!action.secondary)
              .map((action, index) => {
                const isDisabled =
                  typeof action.disabled === "function"
                    ? action.disabled(activeMenuRow)
                    : action.disabled || false;

                return (
                  <Tooltip
                    title={action.tooltip}
                    key={(action.label || action.tooltip) + index}
                  >
                    <MenuItem data-testid={`${testIds.lynksTableRowActionMenuOption}-${action.label}`}
                      onClick={() => {
                        if (!isDisabled) {
                          action.onClick(activeMenuRow);
                        }
                      }}
                      disabled={isDisabled}
                      sx={{
                        opacity: isDisabled ? 0.4 : 1,
                      }}
                    >
                      {action.icon ? (
                        <IconButton data-testid={`${testIds.lynksTableRowActionMenuIcon}-${action.label}`}>{action.icon}</IconButton>
                      ) : null}
                      {action.label}
                    </MenuItem>
                  </Tooltip>
                );
              })
          : null}
      </Menu>
    </Grid>
  );
}

function CustomGridTreeDataGroupingCell(props: GridRenderCellParams) {
  const { id, field, rowNode } = props;
  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector
  );
  const filteredDescendantCount =
    filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleClick: ButtonProps["onClick"] = (event) => {
    if (rowNode.type !== "group") {
      return;
    }

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  return (
    <div>
      {filteredDescendantCount > 0 ? (
        <>
          <Button data-testid={testIds.lynksTableToggleChildRows} onClick={handleClick} tabIndex={-1} size="small">
            {rowNode.type === "group" && rowNode.childrenExpanded ? (
              <ExpandLess />
            ) : (
              <ExpandMore />
            )}
            <Typography>View Children</Typography>
          </Button>
        </>
      ) : (
        <span />
      )}
    </div>
  );
}
